// src/components/games/diceMatch/DiceMatchTrinkets.tsx

import React, { useState, useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonButton,
  IonBadge,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonBackButton,
  IonButtons,
  IonIcon,
  IonAlert,
  IonSpinner,
} from '@ionic/react';
import { star, checkmarkCircle } from 'ionicons/icons';
import {
  TrinketTemplate,
  UserTrinket,
  getRarityLabel,
  getDurationLabel,
  getRarityColor,
} from './trinketTypes';
import {
  getTrinketTemplates,
  getUserTrinkets,
  purchaseTrinket,
  toggleTrinketEquipped,
} from '../../../services/trinketService';
import { getMetaProgression, MetaProgression } from '../../../services/diceMatchService';
import './DiceMatchTrinkets.css';

type ViewMode = 'owned' | 'shop';

const DiceMatchTrinkets: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('owned');
  const [templates, setTemplates] = useState<TrinketTemplate[]>([]);
  const [userTrinkets, setUserTrinkets] = useState<UserTrinket[]>([]);
  const [meta, setMeta] = useState<MetaProgression | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPurchaseAlert, setShowPurchaseAlert] = useState(false);
  const [selectedTrinket, setSelectedTrinket] = useState<TrinketTemplate | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const [templatesData, userTrinketsData, metaData] = await Promise.all([
      getTrinketTemplates(),
      getUserTrinkets(),
      getMetaProgression(),
    ]);

    setTemplates(templatesData);
    setUserTrinkets(userTrinketsData);
    setMeta(metaData);
    setLoading(false);
  };

  const handlePurchaseClick = (template: TrinketTemplate) => {
    setSelectedTrinket(template);
    setShowPurchaseAlert(true);
  };

  const confirmPurchase = async () => {
    if (!selectedTrinket || !meta) return;

    const success = await purchaseTrinket(selectedTrinket.id, selectedTrinket.meta_cost);
    if (success) {
      await loadData();
    }
    setShowPurchaseAlert(false);
    setSelectedTrinket(null);
  };

  const handleToggleEquipped = async (trinketId: string, equipped: boolean) => {
    const success = await toggleTrinketEquipped(trinketId, equipped);
    if (success) {
      await loadData();
    }
  };

  const isOwned = (templateId: string): UserTrinket | undefined => {
    return userTrinkets.find(ut => ut.trinket_id === templateId);
  };

  const canAfford = (cost: number): boolean => {
    return meta ? meta.meta_currency >= cost : false;
  };

  const renderTrinketCard = (template: TrinketTemplate, owned?: UserTrinket) => {
    const rarityColor = getRarityColor(template.rarity);
    const isEquipped = owned?.is_equipped || false;
    const canEquip = template.duration === 'permanent' && owned && !isEquipped;
    const canUnequip = template.duration === 'permanent' && owned && isEquipped;

    return (
      <IonCard key={template.id} className={`trinket-card rarity-${template.rarity}`}>
        <IonCardHeader>
          <div className="trinket-header">
            <div className="trinket-icon">{template.icon}</div>
            <div className="trinket-title-section">
              <IonCardTitle>{template.name}</IonCardTitle>
              <div className="trinket-badges">
                <IonBadge color={rarityColor}>{getRarityLabel(template.rarity)}</IonBadge>
                <IonBadge color="medium">{getDurationLabel(template.duration)}</IonBadge>
                {isEquipped && (
                  <IonBadge color="success">
                    <IonIcon icon={checkmarkCircle} /> Equipped
                  </IonBadge>
                )}
              </div>
            </div>
          </div>
        </IonCardHeader>
        <IonCardContent>
          <p className="trinket-description">{template.description}</p>

          {template.effect_type && (
            <div className="trinket-effect">
              <strong>Effect:</strong> {formatEffect(template)}
            </div>
          )}

          {owned && (
            <div className="trinket-quantity">
              <strong>Quantity:</strong> {owned.quantity}
            </div>
          )}

          {viewMode === 'shop' && (
            <div className="trinket-cost">
              <IonIcon icon={star} color="warning" />
              <span>{template.meta_cost} Meta Currency</span>
            </div>
          )}

          {viewMode === 'shop' && (
            <IonButton
              expand="block"
              onClick={() => handlePurchaseClick(template)}
              disabled={!canAfford(template.meta_cost)}
              color={owned ? 'success' : 'primary'}
            >
              {owned ? 'Buy Another' : 'Purchase'}
            </IonButton>
          )}

          {viewMode === 'owned' && canEquip && (
            <IonButton
              expand="block"
              onClick={() => handleToggleEquipped(template.id, true)}
              color="primary"
            >
              Equip
            </IonButton>
          )}

          {viewMode === 'owned' && canUnequip && (
            <IonButton
              expand="block"
              onClick={() => handleToggleEquipped(template.id, false)}
              color="medium"
            >
              Unequip
            </IonButton>
          )}
        </IonCardContent>
      </IonCard>
    );
  };

  const formatEffect = (template: TrinketTemplate): string => {
    const data = template.effect_data || {};
    switch (template.effect_type) {
      case 'extra_moves':
        return `+${data.moves || 0} moves at start`;
      case 'extra_dice':
        return `+${data.dice || 0} dice at start`;
      case 'reroll':
        return `Reroll current die ${data.uses || 0} times`;
      case 'score_multiplier':
        return `${data.multiplier || 1}x score for this game`;
      case 'currency_multiplier':
        return `${data.multiplier || 1}x in-game currency`;
      case 'guarantee_merge':
        return `Next ${data.merges || 1} merges guaranteed`;
      case 'undo_move':
        return `Undo last move ${data.uses || 1} time(s)`;
      case 'double_next_die':
        return `Place 2 dice instead of 1`;
      case 'wild_die':
        return `Next die can be any type`;
      case 'sparkle_swap':
        return `Swap sparkle with adjacent die once`;
      case 'space_swap':
        return `Swap any two adjacent dice once`;
      case 'skip_die_level':
        return `Merges skip one level`;
      default:
        return 'Special effect';
    }
  };

  const filteredTemplates = viewMode === 'shop'
    ? templates.filter(t => t.meta_cost !== null && t.meta_cost > 0)
    : templates.filter(t => isOwned(t.id));

  const ownedTrinketMap = userTrinkets.reduce((acc, ut) => {
    if (ut.template) {
      acc[ut.trinket_id] = ut;
    }
    return acc;
  }, {} as Record<string, UserTrinket>);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/games/dice-match/menu" />
          </IonButtons>
          <IonTitle>Trinkets</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="trinkets-content">
        <div className="trinkets-container">
          {/* Currency Display */}
          <IonCard className="currency-card">
            <IonCardContent>
              <div className="currency-display">
                <IonIcon icon={star} color="warning" className="currency-icon" />
                <div className="currency-info">
                  <div className="currency-amount">{meta?.meta_currency || 0}</div>
                  <div className="currency-label">Meta Currency</div>
                </div>
              </div>
            </IonCardContent>
          </IonCard>

          {/* View Mode Selector */}
          <IonSegment value={viewMode} onIonChange={e => setViewMode(e.detail.value as ViewMode)}>
            <IonSegmentButton value="owned">
              <IonLabel>My Trinkets ({userTrinkets.length})</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="shop">
              <IonLabel>Shop ({templates.length})</IonLabel>
            </IonSegmentButton>
          </IonSegment>

          {/* Loading State */}
          {loading && (
            <div className="loading-container">
              <IonSpinner name="crescent" />
              <p>Loading trinkets...</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && viewMode === 'owned' && userTrinkets.length === 0 && (
            <IonCard>
              <IonCardContent>
                <div className="empty-state">
                  <div className="empty-icon">🎁</div>
                  <h3>No Trinkets Yet</h3>
                  <p>Purchase trinkets from the shop to enhance your gameplay!</p>
                  <IonButton onClick={() => setViewMode('shop')}>
                    Browse Shop
                  </IonButton>
                </div>
              </IonCardContent>
            </IonCard>
          )}

          {/* Trinket Cards */}
          {!loading && (
            <div className="trinkets-grid">
              {filteredTemplates.map(template =>
                renderTrinketCard(template, ownedTrinketMap[template.id])
              )}
            </div>
          )}

          {/* Info Card */}
          <IonCard className="info-card">
            <IonCardContent>
              <h4>About Trinkets</h4>
              <ul>
                <li><strong>Temporary:</strong> Used once and consumed</li>
                <li><strong>Permanent:</strong> Equip for passive bonuses</li>
                <li><strong>Per-Game:</strong> Activate at start of each game</li>
                <li><strong>Rarity:</strong> Higher rarity = stronger effects</li>
              </ul>
            </IonCardContent>
          </IonCard>
        </div>

        {/* Purchase Confirmation Alert */}
        <IonAlert
          isOpen={showPurchaseAlert}
          onDidDismiss={() => setShowPurchaseAlert(false)}
          header="Purchase Trinket"
          message={
            selectedTrinket
              ? `Purchase ${selectedTrinket.name} for ⭐${selectedTrinket.meta_cost} meta currency?`
              : ''
          }
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
            },
            {
              text: 'Purchase',
              handler: confirmPurchase,
            },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default DiceMatchTrinkets;
